/**
 * The knobs behind the staged bed's pillows (`stagingEuroShamRenderer`,
 * `stagingShamRenderer`, `stagingAccentPillowRenderer`,
 * `stagingLumbarRenderer`), in one place so the renderers and the pillow
 * tuning page read the SAME defaults. The page edits a copy and hands it back
 * as `options.pillowTuning`; a shape found there is a paste over the matching
 * block below — the same loop that finished the towel on a hook
 * (`towelHooksTuning.js`).
 *
 * The defaults are read off Megan's reference photos (2026-10-26). From the
 * side a filled pillow is a FULL OVAL: it stays near its full loft almost to
 * the edge and then curls tightly into the seam, top and bottom alike. The
 * cover gathers into short puckers running in from the seam, and the seam
 * often carries a corded piping. There is no flat border (flange). Two
 * earlier versions were wrong in opposite ways: a slab with rounded corners,
 * then a lens that tapered early (flat from the side) with corners thinned
 * into flat tabs that stuck out like little squares.
 *
 * Lengths are INCHES (the unit pillows are sold in); the rest are unitless.
 *
 * Shared-lib, not the web app, because the staging planner spaces the pillow
 * rows by `pillowDepthM` and runs in the cloud function too.
 */
const {
  seededRandom,
  hashStringToSeed,
} = require('../../geometry/seededRandom');

// Bound once: a global `Math` lookup is not inline-cacheable inside jest's
// `vm` context, which made these per-point loops ~10x slower under test.
const MATH_ABS = Math.abs;
const MATH_ATAN2 = Math.atan2;
const MATH_COS = Math.cos;
const MATH_EXP = Math.exp;
const MATH_FLOOR = Math.floor;
const MATH_MAX = Math.max;
const MATH_MIN = Math.min;
const MATH_POW = Math.pow;
const MATH_ROUND = Math.round;
const MATH_SIGN = Math.sign;
const MATH_SIN = Math.sin;
const MATH_TAN = Math.tan;

const SHARED = {
  // Profile: how long the crown stays full before it turns toward the seam
  // (higher = holds its loft further out), and how it turns (lower = a
  // tighter curl into the seam, i.e. a fuller oval; higher = an early taper).
  crown: 2.6,
  fall: 0.42,
  // Thickness left at the seam, as a fraction of the loft.
  seam: 0.08,
  // Corners: positive keeps them stuffed; negative thins them into flat tabs.
  ears: 0.04,
  // How far the edges draw in between the corners.
  pinch: 0.05,
  // Plan corners: higher is squarer.
  roundness: 5,
  // Creases running in from each corner, and loose folds across the face.
  creaseDepth: 0.3,
  creaseReach: 0.4,
  wrinkleDepth: 0.2,
  wrinkleCount: 4,
  // Puckers where the cover is gathered into the seam: depth (in) and how
  // many round the whole edge.
  gatherDepth: 0.18,
  gatherCount: 80,
  // A soft tap at the middle of the top edge: how far it drops, inches.
  dent: 0,
  // How full the back panel is (1 = as full as the front).
  backFullness: 0.85,
  // Corded piping along the seam (0 off, 1 on).
  piping: 0,
};

const PILLOW_TUNING = Object.freeze({
  euro: Object.freeze({
    ...SHARED,
    // Loft at the centre, and the flat sewn border round the body.
    thickness: 8,
    // No flange on any of them: none of the reference pillows has the flat
    // border, and a wide one read as a picture frame round a cushion. The
    // knob stays for the tuner.
    flange: 0,
    // Back from vertical, resting on the headboard.
    leanDeg: 10,
    // How far the top edge flops forward, inches.
    droop: 0.9,
    piping: 1,
    seed: 3,
  }),
  sham: Object.freeze({
    ...SHARED,
    thickness: 7,
    flange: 0,
    leanDeg: 18,
    droop: 0.7,
    piping: 1,
    seed: 7,
  }),
  // The square decorative pillows in front of the shams.
  accent: Object.freeze({
    ...SHARED,
    thickness: 6.5,
    flange: 0,
    leanDeg: 24,
    droop: 0.5,
    wrinkleCount: 4,
    seed: 13,
  }),
  lumbar: Object.freeze({
    ...SHARED,
    thickness: 5.5,
    flange: 0,
    leanDeg: 26,
    roundness: 5,
    droop: 0.3,
    creaseDepth: 0.25,
    creaseReach: 0.4,
    wrinkleDepth: 0.15,
    wrinkleCount: 3,
    backFullness: 0.8,
    seed: 11,
  }),
});

/**
 * A sofa's pillows are fuller than the bed's (Megan, 2026-10-28, from a
 * staged listing photo): plump in the middle and thin out to the corners,
 * which stand as defined points. The bed's hold their loft almost to the
 * seam, and on a sofa that read as boxes. So the loft tapers from the
 * centre (a lower `crown`), the corners are thinned (`ears` negative) and
 * drawn out to points (a squarer plan and more `pinch`), and the middle is
 * two inches fuller (Megan: then "a little puffier"). Laid over the kind's
 * block by the living-room layout, and by the bed for its decorative front
 * row (Megan, 2026-10-29: the bed's pillows to look like the sofa's).
 */
const SOFA_PILLOW_SHAPE = Object.freeze({
  accent: Object.freeze({
    thickness: 8.5,
    crown: 1.7,
    fall: 0.55,
    seam: 0.05,
    ears: -0.3,
    pinch: 0.1,
    roundness: 8,
    creaseReach: 0.5,
  }),
  lumbar: Object.freeze({
    thickness: 6.5,
    crown: 1.7,
    fall: 0.55,
    seam: 0.05,
    ears: -0.3,
    pinch: 0.08,
    roundness: 8,
  }),
});

/**
 * The bed's back rows, the euros and the shams, in the sofa pillow's OUTLINE
 * and profile: defined, pointed corners, thinned toward the seam (Megan,
 * 2026-10-29: they still had "really, really rounded corners"). That profile
 * tapers from the middle, which left them reading flat at the bed's own
 * loft (Megan: "too flat"), and two inches more was still not enough: her
 * sketch over the side view is a fat lens, close to half as thick as the
 * pillow is tall ("we want these to be fluffy pillows"). So the euro (26 in
 * tall) is 13 in at the centre and the sham 11 in; with the back panel at
 * `backFullness` that draws about 12 and 10. And they LEAN, the euros on the
 * headboard and the shams on the euros (Megan: they were "kind of floating
 * upright" at 10 and 18 degrees).
 */
const { thickness: _sofaFill, ...SOFA_PILLOW_CORNERS } =
  SOFA_PILLOW_SHAPE.accent;
const BED_BACK_ROW_SHAPE = Object.freeze({
  euro: Object.freeze({
    ...SOFA_PILLOW_CORNERS,
    thickness: 13,
    leanDeg: 18,
  }),
  sham: Object.freeze({
    ...SOFA_PILLOW_CORNERS,
    thickness: 11,
    leanDeg: 22,
  }),
});

/** One kind's knobs with a caller's overrides laid over them. */
function resolvePillowTuning(kind, overrides) {
  const base = PILLOW_TUNING[kind];
  if (!base) throw new Error(`resolvePillowTuning: unknown kind "${kind}"`);
  return { ...base, ...(overrides?.[kind] || {}) };
}

/**
 * How deep along the bed a leaning pillow reaches, in METRES, for a standing
 * height in metres: its lean's run plus its loft. The bed layout spaces the
 * rows by this, so a plumper pillow pushes the row in front of it forward
 * instead of sinking into it.
 */
function pillowDepthM(kind, heightM, overrides) {
  const s = resolvePillowTuning(kind, overrides);
  const lean = (s.leanDeg * Math.PI) / 180;
  return heightM * MATH_TAN(lean) + s.thickness * 0.0254 * MATH_COS(lean);
}

const IN_M = 0.0254;

/**
 * Where a point of a pillow lying flat ends up once the cover has settled:
 * the PINCH and DROOP above, and a soft DENT — the top edge tapped down at
 * its middle, so the top reads as two loose ears instead of one straight
 * seam. Stagers' advice now is a gentle tap, not the hard karate chop
 * (Livingetc, Apartment Therapy), and `spec.dent` is how far the middle of
 * the top edge drops, inches (0: none). One function for the cloth and the
 * seam, so the piping stays on the edge.
 */
function settlePillowPoint(x, y, z, halfW, halfL, spec) {
  const u = x / halfW;
  const v = z / halfL;
  const au = MATH_MIN(1, MATH_ABS(u));
  const av = MATH_MIN(1, MATH_ABS(v));
  const top = (-v - 0.5) / 0.5;
  let yy = y;
  let zz = z * (1 - spec.pinch * (1 - au * au));
  if (top > 0) yy += spec.droop * IN_M * top * top;
  const dent = (spec.dent || 0) * IN_M;
  if (dent > 0) {
    // Strongest at the top edge's middle, gone by a third of the way across
    // and halfway down; the fill pushed aside thins the cushion there.
    const g = MATH_EXP(-((u / 0.38) ** 2)) * MATH_MAX(0, (-v - 0.2) / 0.8) ** 2;
    zz += dent * g;
    yy *= 1 - 0.35 * g * MATH_MIN(1, dent / (1.2 * IN_M));
  }
  return { x: x * (1 - spec.pinch * (1 - av * av)), y: yy, z: zz };
}

/**
 * Points on a pillow's FRONT and BACK faces as the renderer draws it, in its
 * box's frame (centre at the origin, y up, z toward the room), before any
 * turn about the vertical: the plan's superellipse, the loft tapering to the
 * seam (`crown`, `fall`, `seam`, thinned corners), the back panel at
 * `backFullness`, all leaned back `leanDeg`. A layout pushes a pillow back
 * until its back touches what is behind it — a headboard, a sofa's back
 * cushion, or the pillow it leans on — and those pillows' fronts are what
 * the next row leans on. Lengths are metres; `n` samples a side. The living
 * room's (`stagingPillowTuning.js` on its branch), moved here so the bed
 * planner can lean on it.
 */
function pillowFaces(kind, box, overrides, n = 21) {
  const s = resolvePillowTuning(kind, overrides);
  const lean = (s.leanDeg * Math.PI) / 180;
  const t = s.thickness * IN_M;
  const L = box.y / MATH_COS(lean);
  const k = s.roundness;
  const front = [];
  const back = [];
  const a = Math.PI / 2 - lean;
  const up = (q) => [
    q.x,
    q.y * MATH_COS(a) - q.z * MATH_SIN(a),
    q.y * MATH_SIN(a) + q.z * MATH_COS(a),
  ];
  // Lying flat (thickness on y, the top edge at -z), settled as drawn, then
  // stood up: turned (90 deg - lean) about x.
  const addPoint = (u, v, rho) => {
    const base = MATH_POW(MATH_MAX(0, 1 - MATH_POW(rho, s.crown)), s.fall);
    const ear =
      s.ears * MATH_SIN(2 * MATH_ATAN2(MATH_ABS(v), MATH_ABS(u))) ** 2;
    const loft =
      (t / 2) * MATH_MAX(s.seam, base * (1 - s.seam) + s.seam + ear * rho ** 4);
    const x = (u * box.x) / 2;
    const z = (v * L) / 2;
    const settle = (y) => settlePillowPoint(x, y, z, box.x / 2, L / 2, s);
    front.push(up(settle(loft)));
    back.push(up(settle(-loft * s.backFullness)));
  };
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const u = -1 + (2 * i) / (n - 1);
      const v = -1 + (2 * j) / (n - 1);
      const rho = MATH_POW(MATH_ABS(u) ** k + MATH_ABS(v) ** k, 1 / k);
      if (rho <= 1) addPoint(u, v, rho);
    }
  }
  // The seam itself, which a grid misses at the corners: the superellipse
  // `pillowGeometry` draws, where a tilted pillow's lowest point is.
  for (let i = 0; i < 4 * n; i++) {
    const th = (i / (4 * n)) * Math.PI * 2;
    const c = MATH_COS(th);
    const sn = MATH_SIN(th);
    addPoint(
      MATH_SIGN(c || 1) * MATH_ABS(c) ** (2 / k),
      MATH_SIGN(sn || 1) * MATH_ABS(sn) ** (2 / k),
      1
    );
  }
  return { front, back };
}

/**
 * One pillow's OWN shape, so no two are the same pillow stamped out
 * (Yu-Han, 2026-10-28: "less rectilinear"; perfectly matched upholstery is
 * what reads as computer-made — CGIFurniture): its own creases, a top that
 * flops more or less, a few degrees more or less lean, a little more or
 * less fill, and about half tapped down at the top (`dent`). Seeded by the
 * pillow's key, so a bed keeps its pillows. The living room's rule
 * (`placePillows` there), here so the planner can carry it on the record.
 *
 * @param {string} kind  a `PILLOW_TUNING` kind
 * @param {string} key  the pillow's stable key
 * @param {number} looseness  0 leaves the kind's shape as is
 * @param {object} [overrides]  per-kind tuning the shape starts from
 * @returns {object} only the knobs that differ from the kind's block, to be
 *   laid over it as `{ [kind]: own }`
 */
function ownPillowShape(kind, key, looseness, overrides) {
  const rand = seededRandom(hashStringToSeed(`${key}:shape`));
  const r = () => (rand() * 2 - 1) * looseness;
  const base = resolvePillowTuning(kind, overrides);
  const tapped = rand() < 0.5;
  const round = (n) => MATH_ROUND(n * 1000) / 1000;
  return {
    ...(overrides?.[kind] || {}),
    seed: 1 + MATH_FLOOR(rand() * 1000),
    droop: round(base.droop * (1 + 0.6 * r())),
    leanDeg: round(base.leanDeg + 4 * r()),
    thickness: round(base.thickness * (1 + 0.08 * r())),
    dent: tapped ? round(looseness * (0.6 + 0.5 * rand())) : base.dent,
  };
}

module.exports = {
  PILLOW_TUNING,
  SOFA_PILLOW_SHAPE,
  BED_BACK_ROW_SHAPE,
  resolvePillowTuning,
  pillowDepthM,
  settlePillowPoint,
  pillowFaces,
  ownPillowShape,
};
